/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be

import io.peekandpoke.klang.audio_be.utils.fastExp
import io.peekandpoke.klang.audio_bridge.AdsrCurve
import io.peekandpoke.klang.audio_bridge.constants.ADSR_EXP_K

// ─────────────────────────────────────────────────────────────────────────────
// The curve math of the envelope law (`EnvelopeCore`, which every ADSR envelope hosts, the voice's pitch
// envelope included since phase 3 step 5b (c1)):
// the stage shapes, the release time base, and the de-click coefficient.
//
// The tunable values themselves (ADSR_EXP_K, ENV_DECLICK_SECONDS) live in
// `audio_bridge/constants/EnvelopeDefaults.kt`, which the authoring side reads too, so both sides read one
// declaration. Only the math lives here.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalisation factor for [adsrExpShape] at curvature [k] — makes `g(0)=0`, `g(1)=1`. Through
 * [fastExp] like the shape itself, so `g(1)` is `A * (1 / A)` of one value: 1.0 or one ulp under
 * it (a reciprocal times its base is not exact; k = 7 lands on 0.9999999999999999, the library
 * `exp` had its own such k). `g(0)` IS exactly 0: `fastExp(0.0)` is exactly 1, which the release's
 * last frame relies on.
 */
@Suppress("NOTHING_TO_INLINE")
internal inline fun adsrExpNorm(k: Double): Double = 1.0 / (fastExp(k) - 1.0)

/** Normalisation for the global-default curvature [ADSR_EXP_K]. */
@PublishedApi
internal val ADSR_EXP_NORM: Double = adsrExpNorm(ADSR_EXP_K)

/**
 * True-exponential ADSR shape `g(x) = (e^(K·x) − 1)/(e^K − 1)` on `x ∈ [0,1]` at curvature [k], with
 * the precomputed [norm] = [adsrExpNorm]\(k\); `g(0)=0`, `g(1)=1`. Convex (like `Square` but
 * longer-tailed). For decay / release the caller passes `omp = 1−p`, giving the natural "fast drop,
 * long tail". Every envelope passes [ADSR_EXP_K].
 *
 * NOTE: one [fastExp] per call (since 2026-09-15; it was a library `exp`, one transcendental
 * per sample in every renderer, the rest of the curve family being multiply-only). The next
 * step down, if the curve ever shows up again, is a recursive multiply-only one-pole per stage.
 */
@Suppress("NOTHING_TO_INLINE")
internal inline fun adsrExpShape(x: Double, k: Double, norm: Double): Double = (fastExp(k * x) - 1.0) * norm

/**
 * THE shape of one ADSR stage at linear progress [x] in 0..1, for every [AdsrCurve]: the one `when`
 * of the engine, evaluated by `EnvelopeCore` for every envelope. An attack passes `p`; a decay or
 * release passes `omp = 1 - p`, which gives the falling shapes. [k] and [norm] are the Exponential
 * curvature and its [adsrExpNorm].
 *
 * `Linear` returns [x] itself, bit for bit.
 *
 * **The statement form, on purpose** (engine follow-ups 10a and 10b, 2026-10-10): three of its hosts call this per
 * sample (the chain `adsr`, the pitch and the FM index envelopes; the filter envelope, [EnvelopeCore.prepare] and
 * [EnvelopeCore.releaseEndLevel] once per block), and on V8 the expression form of an exhaustive `when` boxes its
 * double result in a sample loop, one heap number per sample
 * (`audio/ref/performance.md`, the rule on the exhaustive `when` in a sample loop; the stated exception to
 * `/code-style` section 19). The `when` stays exhaustive, so a new curve without an arm does not compile, but the
 * compiler does not check that an arm ASSIGNS `y`: `EnvelopeLawSpec` ("each stage takes its own curve") renders
 * every arm against its oracle and is the guard. Do not fold it back into an expression.
 */
@Suppress("NOTHING_TO_INLINE")
internal inline fun adsrCurveShape(curve: AdsrCurve, x: Double, k: Double, norm: Double): Double {
    // Initialized, so no path leaves it unassigned (the statement form; the KDoc says why).
    var y = 0.0

    when (curve) {
        AdsrCurve.Linear -> y = x
        AdsrCurve.Square -> y = x * x
        AdsrCurve.Cube -> y = x * x * x
        AdsrCurve.SCurve -> y = if (x < 0.5) 2.0 * x * x else 1.0 - 2.0 * (1.0 - x) * (1.0 - x)
        AdsrCurve.InvSquare -> y = x * (2.0 - x)
        AdsrCurve.Exponential -> y = adsrExpShape(x = x, k = k, norm = norm)
    }

    return y
}

// ─────────────────────────────────────────────────────────────────────────────
// Release progress — the time base every release stage shares.
//
// A release of N frames renders relPos = 0 .. N-1. Dividing by N therefore tops
// p out at (N-1)/N and the curve's exact endpoint g(0)=0 lands on the first frame
// the voice does NOT render. The envelope is then still audible when the voice is
// dropped: measured on the exp curve, 6.6e-5 (-84 dB) at a 50 ms release, but
// 5.9e-2 (-25 dB) at 0.1 ms. That residual is a step straight to zero.
//
// Dividing by N-1 lands p = 1.0 exactly on the last rendered frame, so the CURVE
// ends on 0.0. Both endpoints are then exact: p=0 at gate end, p=1 at the final frame.
//
// Scope, measured: on the ignitor envelope (AdsrIgnitor, where declick defaults
// to 0 = off) the rendered gain reaches 0.0 too, and that is the path Der Schmetterling's
// guitars clicked on. With the de-click one-pole on (`classic()`'s envelope, as on the retired strip VCA)
// it sits DOWNSTREAM of the curve, lagging ~47 frames at ENV_DECLICK_SECONDS, so the gain on the last
// frame only moves 3.38e-3 -> 3.31e-3 at a 50 ms release: the curve residual was never the
// dominant term there. Fixing THAT is a separate question (it changes every song's note-off)
// and is deliberately not attempted here.
//
// Returned as a hoistable (offset, denominator) pair so the hot loops hoist the
// branch out and keep one divide per sample, exactly as before.
//
// It MUST stay a divide. Hoisting a reciprocal and multiplying looks free but is not
// exact: 239 * (1.0/239.0) is 0.9999999999999999, so `omp` comes out 1.1e-16 instead
// of 0 and the endpoint is missed all over again by a hair. Division of equal values
// is exact. `ReleaseEndsAtZeroSpec` asserted `shouldBe 0.0` with no tolerance and caught precisely
// this; its rows live on in `EnvelopeLawSpec` (the core, every curve) and `BareTreeVoiceSpec` (a
// 240-frame exponential release through a voice, the 239 above) since 2026-09-27.
//
// The degenerate N<=1 case (a release too short to ramp) is folded into the pair as
// offset=1, denom=1, making p=1 on its single frame instead of leaving the gain at full.
// ─────────────────────────────────────────────────────────────────────────────

/** Hoisted denominator for the release ramp — `N-1`, see the note above. */
@Suppress("NOTHING_TO_INLINE")
internal inline fun releaseProgressDenom(releaseFrames: Double): Double =
    if (releaseFrames > 1.0) releaseFrames - 1.0 else 1.0

/** Hoisted offset partner of [releaseProgressDenom]; `p = ((relPos + offset) / denom).coerceAtMost(1.0)`. */
@Suppress("NOTHING_TO_INLINE")
internal inline fun releaseProgressOffset(releaseFrames: Double): Double =
    if (releaseFrames > 1.0) 0.0 else 1.0

// ─────────────────────────────────────────────────────────────────────────────
// De-click smoother on the final amplitude-envelope gain.
//
// The shape curves are C0-continuous (the value reaches its endpoints exactly)
// but NOT C1-continuous: at a segment join (attack→decay peak, gate-off,
// release→silence) the gain changes slope abruptly. That corner is a fixed-size
// event that radiates a broadband click; on a low note the slow carrier can't
// mask it, so it reads as a "plop", while a high note's fast carrier hides it.
// A short one-pole low-pass on the gain rounds the corner without altering the
// envelope's character.
//
// The time constant itself is ENV_DECLICK_SECONDS, in audio_bridge/constants — see its
// KDoc for the corner/floor measurement rather than repeating it here.
//
// Its per-sample coefficient is the one-pole time constant `timeConstantCoeff` (`utils/time_constant.kt`),
// derived once per block by the envelope node.
// ─────────────────────────────────────────────────────────────────────────────
